import React, { FunctionComponent, useEffect, useState } from 'react'
import { Checkbox, Col, Form, Input, Radio, Row, Select, Switch, Tooltip, Typography } from 'antd'
import { FieldType, TBrandCustomField } from '../../../context/types'
import { Wysiwyg } from './Wysiwyg/Wysiwyg'
import { useCustomFieldsContext } from '../../../context/customFields'
import { InfoCircleOutlined } from '@ant-design/icons'
import { selectAllValue } from '../../../utils/custom-fields'
import './CustomField.less'

const { Option } = Select
const { Title } = Typography

type TProps = {
	field: TBrandCustomField
	onChange?: (value: any) => void
	disabled?: boolean
	value?: string
	form?: any
	desktopSpan?: number
}

const CustomField: FunctionComponent<TProps> = (props) => {
	const { field, form, desktopSpan, disabled, onChange } = props
	const {
		customFields: { fields },
	} = useCustomFieldsContext()
	const [isFieldHidden, setIsFieldHidden] = useState<boolean>(false)
	const checkFieldVisibility = () => {
		const mainField = fields[field.context?.condition?.field_id]
		const currentValue = form.getFieldsValue()[fields[mainField.id]?.name]
		if (mainField.type === FieldType.multiCheckbox) {
			return field.context?.condition?.answer?.some((answerValueItem) =>
				currentValue?.some((currentValueItem: any) => currentValueItem === answerValueItem)
			)
		} else if (mainField.type === FieldType.radio) {
			return field.context?.condition?.answer?.includes(currentValue)
		}

		return true
	}

	useEffect(() => {
		setIsFieldHidden(field.context?.condition && !checkFieldVisibility())
	}, [form.getFieldsValue()])

	const Label = () => {
		return (
			<span>
				{field.label}
				{}
				{field.context?.tooltip ? (
					<>
						{' '}
						<Tooltip title={field.context?.tooltip}>
							<InfoCircleOutlined />
						</Tooltip>
					</>
				) : null}
			</span>
		)
	}

	const getField = () => {
		switch (field.type) {
			case FieldType.text:
				return <Input placeholder={field.context?.placeholder} onChange={onChange} disabled={disabled} />
			case FieldType.number:
				return (
					<Input
						type="number"
						placeholder={field.context?.placeholder}
						max={field.context?.max}
						min={field.context?.min}
						onChange={onChange}
						disabled={disabled}
					/>
				)
			case FieldType.select:
				return (
					<Select placeholder={field.context?.placeholder} onChange={onChange} disabled={disabled}>
						{field.context.options?.map(({ text, value }) => (
							<Option key={value} value={value}>
								{text}
							</Option>
						))}
					</Select>
				)
			case FieldType.radio:
				return (
					<Radio.Group
						onChange={onChange}
						disabled={disabled}
						className={field.context.options.length > 3 ? 'radio-group_column' : 'radio-group_row'}
					>
						{field.context.options?.map(({ text, value }) => (
							<Radio key={value} value={value}>
								{text}
							</Radio>
						))}
					</Radio.Group>
				)
			case FieldType.checkbox:
				return <Switch onChange={onChange} disabled={disabled} defaultChecked={Boolean(field.value)} />
			case FieldType.wysiwyg:
				return <Wysiwyg placeholder={field.context?.placeholder} onChange={onChange} disabled={disabled} />
			case FieldType.multiCheckbox:
				return (
					<Checkbox.Group onChange={onChange} disabled={disabled}>
						<Row gutter={[20, 0]}>
							{!!field.context?.select_all && (
								<Col xs={24} lg={12}>
									<Checkbox value={selectAllValue}>Select All</Checkbox>
								</Col>
							)}
							{field.context.options?.map(({ text, value }) => (
								<Col xs={24} lg={12} key={value}>
									<Checkbox value={value}>{text}</Checkbox>
								</Col>
							))}
						</Row>
					</Checkbox.Group>
				)
			case FieldType.multiSelect:
				return (
					<Select
						mode="multiple"
						maxTagCount="responsive"
						placeholder={field.context?.placeholder}
						onChange={onChange}
						disabled={disabled}
					>
						{field.context.options?.map(({ text, value }) => (
							<Option key={value} value={value}>
								{text}
							</Option>
						))}
					</Select>
				)
			default:
				return null
		}
	}

	if (isFieldHidden) {
		return null
	}

	if (field.type === FieldType.label) {
		return (
			<Col xs={24} lg={24}>
				<Title level={4}>{field.label}</Title>
			</Col>
		)
	}

	return (
		<Col xs={24} lg={desktopSpan}>
			<Form.Item
				name={field.name}
				label={<Label />}
				valuePropName={field.type === FieldType.checkbox ? 'checked' : 'value'}
				rules={[
					{
						required: field.is_required,
						message: `Field ${field.label} is required.`,
					},
				]}
			>
				{getField()}
			</Form.Item>
		</Col>
	)
}

export default CustomField
